import React, {
    ChangeEvent,
    Dispatch,
    FC,
    FormEvent,
    ReactNode,
    SetStateAction,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { Agency } from '../../types/Agency';
import ApiService from '../../ApiService';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertColor,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Modal,
    Paper,
    Select,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GridColDef, GridFooterContainer, GridRenderCellParams, GridSortModel, GridToolbar } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid2';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import {
    CancelOutlined,
    CloseOutlined,
    ContentCopy as ContentCopyIcon,
    CloudUploadOutlined,
    ContentCopy,
    EmailOutlined,
    EmojiTransportationOutlined,
    InfoOutlined,
    KeyOutlined,
    LocalOfferOutlined,
    RssFeedOutlined,
    SaveOutlined,
    UploadFile,
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { timezones } from '../../types/Date';
import { Dealer } from '../../types/Dealer';
import HatTrackingStatusCell from './HatTrackingStatusCell';
import DspAdvertiserStatusCell from './DspAdvertiserStatusCell';
import AgencyAdvertiserExportCsvButton from './AgencyAdvertiserExportCsvButton';
import { useQuery } from '@tanstack/react-query';
import FormSection from '../../components/FormSection';
import QuickSightDashboardSelector from '../../components/QuickSightDashboardSelector';
import { TabsContainer } from '../../components/Tabs';
import TabPanel from '../../components/TabPanel';
import { DspEntity, ShellCampaign } from '../../types/DspEntity';
import { SasAccount } from '../../types/SasAccount';
import AccessGuard, { hasAccess } from '../../components/AccessGuard';
import AgencyGatewayAPI from './AgencyGatewayAPI';
import { UserContext } from '../../App';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../types/User';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import ActiveStatus from '../../components/ActiveStatus';
import Block from '../../components/Block';
import CDGridPro from '../../components/CDGridPro';
import Column from '../../components/Column';
import Row from '../../components/Row';
import CampaignFeesComponent from '../campaignManager/campaignDetailView/CampaignFeesComponent';
import { CampaignFee } from '../../types/Campaign';
import AccessPermissions from './partials/AccessPermissions';
import FirstPartyDataIntegration from './FirstPartyDataIntegrationTab';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import SyncIcon from '@mui/icons-material/Sync';
import Utils from '../../components/Utils';
import { transformSortModelForBackend } from './partials/SortModelTransformer';
import DeleteIcon from '@mui/icons-material/Delete';
import usePageTitle from '../../hooks/usePageTitle';
import OemAutocomplete from '../../components/Form/Fields/OemAutocomplete';
import OemProgramAutocomplete from '../../components/Form/Fields/OemProgramAutocomplete';
import BrandAutocomplete from '../../components/Form/Fields/BrandAutocomplete';
import { useUserContext } from '../../hooks/Contexts/UserContext';
import Collapse from '@mui/material/Collapse';
import {
    AD_SERVING_PREFIX,
    extractFolderName,
    extractSuffix,
    normalizeAgencyFolderName,
} from '../../utils/AdServingFolderPathUtils';
import AgencyDspIntegrationMap from './AgencyDspIntegrationMap';
import SelectPlatformsDialog, { PLATFORMS, PlatformKey } from './SelectPlatformsDialog';
import SelectReportsDialog, { REPORTS } from './SelectReportsDialog';
/**
 * used to identify tab names for URI and page titles
 */
const TABS = {
    'agency-settings': { label: 'Agency Settings', slug: 'agency-settings' },
    advertisers: { label: 'Advertisers', slug: 'advertisers' },
    'campaign-parameters': { label: 'Campaign Parameters', slug: 'campaign-parameters' },
    fees: { label: 'Fees', slug: 'fees' },
    'first-party-data': { label: 'First Party Data', slug: 'first-party-data' },
    'access-permissions': { label: 'Access Permissions', slug: 'access-permissions' },
    'dsp-integration-map': { label: 'Dsp Integration Map', slug: 'dsp-integration-map' },
    'reporting-exports': { label: 'Reporting Exports', slug: 'reporting-exports' },
} as const;

type TabSlug = keyof typeof TABS;

/**
 * used to check whether parameter is an actual tab name
 */
function isTabSlug(value: string): value is TabSlug {
    return value in TABS;
}

// identifies the default tab to load
const DEFAULT_TAB_ID = TABS['agency-settings'].slug;

/**
 * field group interface for OEM, BRAND, OEM PROGRAM settings
 */
interface AgencyOemProgramFieldsetProps {
    agency: Agency;
    setAgency: Dispatch<SetStateAction<Agency>>;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * field group for OEM, BRAND, OEM PROGRAM settings (UI group)
 */
const AgencyOemProgramFieldset: FC<AgencyOemProgramFieldsetProps> = ({ agency, setAgency, handleChange }) => {
    const { userContext } = useUserContext();

    const oems = agency?.oems?.map((v: any) => v.oemId) || [];
    const brands = agency?.brands?.map((v: any) => v.brandId) || [];
    const oemPrograms = agency?.oemPrograms?.map((v: any) => v.id) || [];

    const isOemsFieldDisabled = !userContext.isAdmin() && !userContext.isOpsAdmin();
    const isBrandsFieldDisabled = (!userContext.isAdmin() && !userContext.isOpsAdmin()) || oems.length === 0;
    const isOemProgramsFieldDisabled =
        (!userContext.isAdmin() && !userContext.isOpsAdmin()) || isBrandsFieldDisabled || brands.length === 0;

    return (
        <Grid container spacing={3}>
            <Grid size={12}>
                <OemAutocomplete
                    label="Select OEMs"
                    multiple
                    disabled={isOemsFieldDisabled}
                    value={oems}
                    onChange={(value: any) => {
                        setAgency((prev) => {
                            return {
                                ...prev,
                                oems: value.map((v: any) => ({
                                    oemId: v.id,
                                    agencyId: agency.id,
                                })),
                            };
                        });
                    }}
                />
            </Grid>
            <Grid size={12}>
                <Tooltip
                    arrow
                    disableHoverListener={!isBrandsFieldDisabled}
                    title="Please fill out OEMs in order to edit this setting."
                >
                    <Box>
                        <BrandAutocomplete
                            label="Select Brands"
                            multiple
                            disabled={isBrandsFieldDisabled}
                            value={brands}
                            criteria={{
                                oemIds: agency?.oems?.map((v: any) => v.oemId) || undefined,
                            }}
                            onChange={(value: any) => {
                                setAgency((prev) => {
                                    return {
                                        ...prev,
                                        brands: value.map((v: any) => ({
                                            brandId: v.id,
                                            agencyId: prev.id,
                                        })),
                                    };
                                });
                            }}
                        />
                    </Box>
                </Tooltip>
            </Grid>
            <Grid size={12}>
                <Tooltip
                    arrow
                    disableHoverListener={!isOemProgramsFieldDisabled}
                    title="Please fill out OEMs and Brands in order to edit this setting."
                >
                    <Box>
                        <OemProgramAutocomplete
                            label="Select OEM Programs"
                            multiple
                            disabled={isOemProgramsFieldDisabled}
                            value={oemPrograms}
                            criteria={{
                                oemIds: agency?.oems?.map((v: any) => v.oemId) || undefined,
                                brandIds: agency?.brands?.map((v: any) => v.brandId) || undefined,
                            }}
                            onChange={(value: any) => {
                                setAgency((prev) => {
                                    return {
                                        ...prev,
                                        oemPrograms: value.map((v: any) => ({
                                            id: v.id,
                                            agencyId: agency.id,
                                        })),
                                    };
                                });
                            }}
                        />
                    </Box>
                </Tooltip>
            </Grid>

            {/**
             *  Toggle - show oem filters in measurement studio
             *
             */}
            <Grid size={12}>
                <Box
                    sx={{
                        mt: 1,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'right',
                    }}
                >
                    <Box>
                        <Typography variant="body2">Show OEM Filters for Agency Users</Typography>
                    </Box>

                    <Tooltip title='When enabled, users assigned this agency will be able to see the "OEM" and "OEM Program" filters within Measurement Studio.'>
                        <Switch
                            checked={agency.activeOem}
                            onChange={handleChange}
                            name={'activeOem'}
                            data-testid="enable-oem-filters"
                        />
                    </Tooltip>
                </Box>
            </Grid>
        </Grid>
    );
};

/**
 * Sender Email verification section within white-label settings.
 * Allows admins to configure a custom From address for agency emails via AWS SES domain verification.
 */
interface DnsRecord {
    type: string;
    name: string;
    value: string;
}

interface SenderEmailSectionProps {
    agency: Agency;
    setAgency: Dispatch<SetStateAction<Agency>>;
    showInfoMessage: (alertColor: AlertColor, message: string) => void;
}

const SenderEmailSection: FC<SenderEmailSectionProps> = ({ agency, setAgency, showInfoMessage }) => {
    const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
    const [, setVerificationStatus] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showDnsRecords, setShowDnsRecords] = useState(false);

    const isVerified = agency.senderEmailDomainVerified === true;
    const isPending = !isVerified && dnsRecords.length > 0;
    const hasSenderEmail = !!agency.senderEmail && agency.senderEmail.trim().length > 0;
    const isNewAgency = !agency.id;

    const handleSenderEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newEmail = event.target.value;
        const oldDomain = agency.senderEmail ? agency.senderEmail.split('@')[1] : '';
        const newDomain = newEmail.includes('@') ? newEmail.split('@')[1] : '';

        setAgency((prev) => ({
            ...prev,
            senderEmail: newEmail,
            // Reset verification if domain changes
            ...(oldDomain && newDomain && oldDomain !== newDomain ? { senderEmailDomainVerified: false } : {}),
        }));

        // Clear DNS records if domain changes or email is cleared
        if (!newEmail.trim() || (oldDomain && newDomain && oldDomain !== newDomain)) {
            setDnsRecords([]);
            setVerificationStatus(null);
        }
    };

    const handleVerifyDomain = async () => {
        if (!agency.id) return;
        setLoading(true);
        try {
            const response = await ApiService.verifySenderDomain(agency.id, agency.senderEmail!);
            const result = response.data;
            setDnsRecords(result.dnsRecords || []);
            setVerificationStatus(result.status);
            setShowDnsRecords(true);
            if (result.status === 'Success') {
                setAgency((prev) => ({ ...prev, senderEmailDomainVerified: true }));
                showInfoMessage('success', 'Domain is already verified!');
            } else {
                showInfoMessage(
                    'info',
                    'DNS records generated. Add them to your domain registrar to complete verification.'
                );
            }
        } catch (error: any) {
            const message = error.response?.data?.error || 'Failed to initiate domain verification. Please try again.';
            showInfoMessage('error', message);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckStatus = async () => {
        if (!agency.id) return;
        setLoading(true);
        try {
            const response = await ApiService.getSenderDomainStatus(agency.id, agency.senderEmail!);
            const result = response.data;
            setVerificationStatus(result.status);
            setDnsRecords(result.dnsRecords || []);
            if (result.status === 'Success') {
                setAgency((prev) => ({ ...prev, senderEmailDomainVerified: true }));
                showInfoMessage(
                    'success',
                    'Domain verified successfully! Emails will now be sent from your custom address.'
                );
            } else {
                showInfoMessage('warning', 'Domain not yet verified. DNS propagation can take up to 72 hours.');
            }
        } catch (error: any) {
            const message = error.response?.data?.error || 'Failed to check verification status.';
            showInfoMessage('error', message);
        } finally {
            setLoading(false);
        }
    };

    const handleSendTestEmail = async () => {
        if (!agency.id) return;
        setLoading(true);
        try {
            await ApiService.sendTestSenderEmail(agency.id);
            showInfoMessage('success', 'Test email sent! Check your inbox.');
        } catch (error: any) {
            const message = error.response?.data?.error || 'Failed to send test email. Please try again.';
            showInfoMessage('error', message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showInfoMessage('success', 'Copied to clipboard');
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <EmailOutlined fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                    Custom Sender Email
                </Typography>
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                    Optional
                </Typography>
                {isVerified && <Chip label="Verified" color="success" size="small" />}
                {isPending && <Chip label="Pending Verification" color="warning" size="small" />}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure a custom email address for platform emails sent to this agency's users (e.g., password resets,
                export notifications). Requires domain verification through AWS SES.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <TextField
                    name="senderEmail"
                    label="Sender Email Address"
                    value={agency.senderEmail || ''}
                    placeholder="noreply@yourdomain.com"
                    fullWidth
                    size="small"
                    variant="outlined"
                    onChange={handleSenderEmailChange}
                    helperText="The email address platform emails will be sent from (e.g., noreply@yourdomain.com)"
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 255 },
                    }}
                />

                {!isVerified && (
                    <Button
                        variant="outlined"
                        onClick={isPending ? handleCheckStatus : handleVerifyDomain}
                        disabled={!hasSenderEmail || isNewAgency || loading}
                        startIcon={loading ? <CircularProgress size={16} /> : undefined}
                        sx={{ minWidth: 160, mt: 0.25 }}
                    >
                        {isPending ? 'Check Status' : 'Verify Domain'}
                    </Button>
                )}

                {isVerified && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSendTestEmail}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <EmailOutlined />}
                        sx={{ minWidth: 160, mt: 0.25 }}
                    >
                        Send Test Email
                    </Button>
                )}
            </Box>

            {/* Verified success banner */}
            {isVerified && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <strong>Domain Verified</strong> — Emails to users of this agency will be sent from{' '}
                    <strong>
                        {agency.name} &lt;{agency.senderEmail}&gt;
                    </strong>
                </Alert>
            )}

            {/* Pending verification banner */}
            {isPending && !isVerified && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>DNS Configuration Required</strong> — Add the following DNS records at your domain registrar
                    to verify ownership and enable email sending. DNS propagation can take up to 72 hours.
                </Alert>
            )}

            {/* DNS Records Table */}
            {dnsRecords.length > 0 && (
                <Box>
                    {isVerified ? (
                        <Box sx={{ cursor: 'pointer', mb: 1 }} onClick={() => setShowDnsRecords(!showDnsRecords)}>
                            <Typography variant="caption" color="text.secondary">
                                {showDnsRecords ? 'Hide' : 'Show'} DNS Records
                            </Typography>
                        </Box>
                    ) : null}

                    {(showDnsRecords || !isVerified) && (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 600, width: 70 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Host / Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                        <TableCell sx={{ width: 40 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dnsRecords.map((record, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Chip
                                                    label={record.type}
                                                    size="small"
                                                    color={record.type === 'CNAME' ? 'primary' : 'secondary'}
                                                    variant="outlined"
                                                    sx={{ fontSize: 11 }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 11,
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {record.name}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 11,
                                                    wordBreak: 'break-all',
                                                }}
                                            >
                                                {record.value}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyToClipboard(record.value)}
                                                    title="Copy value"
                                                >
                                                    <ContentCopy sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}
        </Box>
    );
};

export type AgencyUpdateCallback = (name: string, changes: Partial<Agency>) => void;

function ShellCampaignsGridFooter(props: { totalElements: number; numberOfElements: number }) {
    return (
        <GridFooterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

export default function AgencyEditPage() {
    const { userContext } = useContext(UserContext);
    const params = useParams();
    const id = params.id === 'new' ? null : params.id ? +params.id : null;
    const navigate = useNavigate();
    const [agency, setAgency] = useState<Agency>(new Agency());
    const [dealers, setDealers] = useState<Dealer[]>([]);
    const [selectedDealers, setSelectedDealers] = useState<Dealer[]>([]);
    const [selectReportsDialogOpen, setSelectReportsDialogOpen] = useState(false);
    const [selectOnDemandReportsDialogOpen, setSelectOnDemandReportsDialogOpen] = useState(false);
    const [selectScheduledPlatformsDialogOpen, setSelectScheduledPlatformsDialogOpen] = useState(false);
    const [selectOnDemandPlatformsDialogOpen, setSelectOnDemandPlatformsDialogOpen] = useState(false);
    const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
    const [selectedOnDemandReports, setSelectedOnDemandReports] = useState<Set<string>>(new Set());
    const [selectedScheduledPlatforms, setSelectedScheduledPlatforms] = useState<Set<PlatformKey>>(new Set());
    const [selectedOnDemandPlatforms, setSelectedOnDemandPlatforms] = useState<Set<PlatformKey>>(new Set());
    const [savedApiAccessState, setSavedApiAccessState] = useState<{
        activeApiDocs: boolean;
        apiIncludeCostMetrics: boolean;
        apiKey: string | null;
    }>({ activeApiDocs: false, apiIncludeCostMetrics: false, apiKey: null });
    const [dealerIds, setDealerIds] = useState<number[]>([]);

    // All DSP entities (separated by platform for UI purposes)
    const [amazonDspEntities, setAmazonDspEntities] = useState<DspEntity[]>([]);
    const [dv360Entities, setDv360Entities] = useState<DspEntity[]>([]);

    // Selected DSP entity IDs (from both platforms)
    const [amazonDspEntityIds, setAmazonDspEntityIds] = useState<number[]>([]);
    const [dv360EntityIds, setDv360EntityIds] = useState<number[]>([]);

    // Bulk-resolved Amazon DSP advertiser names ({dspAdvertiserId -> name}) for the advertisers
    // table. One cached request per agency instead of a live lookup per row.
    const { data: dspAdvertiserNames, isLoading: isLoadingDspAdvertiserNames } = useQuery({
        queryKey: ['agencyDspAdvertiserNames', agency.id],
        queryFn: () => ApiService.getAgencyDspAdvertiserNames(agency.id as number).then((res) => res.data),
        enabled: !!agency.id && amazonDspEntityIds.length > 0,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const [sasAccounts, setSasAccounts] = useState<SasAccount[]>([]);
    const [sasAccountIds, setSasAccountIds] = useState<number[]>([]);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
    const [isTermAccepted, setIsTermAccepted] = useState<boolean>(false);
    const [selectedMonth, setSelectedMonth] = useState<number>(1);
    const [sourceFolderError, setSourceFolderError] = useState<string | null>(null);
    const [imageSourceFolderError, setImageSourceFolderError] = useState<string | null>(null);

    const initialTabId = params.tab && isTabSlug(params.tab) ? params.tab : DEFAULT_TAB_ID;
    const [selectedTab, setSelectedTab] = useState<TabSlug>(initialTabId);

    // build and render title
    const agencyName = agency?.name;
    const segment = !params.id ? 'Add New Agency' : TABS[selectedTab].label;
    usePageTitle({ entity: agencyName, segments: [segment] });

    /**
     * hook for tab navigation
     */
    useEffect(() => {
        if (!params.tab) return;

        if (!isTabSlug(params.tab)) {
            navigate(`/agencies/${params.id}/${DEFAULT_TAB_ID}`, { replace: true });
        } else {
            setSelectedTab(params.tab);
        }
    }, [params.tab, navigate, params.id]);

    /**
     * hook that runs on mount (page load)
     */
    useEffect(() => {
        if (id) {
            ApiService.getAgencyAvailableDealers(id).then((response) => {
                setDealers(response.data);
            });
            ApiService.getAgency(id)
                .then((response) => {
                    let agency = response.data;
                    setAgency(agency);
                    setSavedApiAccessState({
                        activeApiDocs: agency.activeApiDocs ?? false,
                        apiIncludeCostMetrics: agency.apiIncludeCostMetrics ?? false,
                        apiKey: agency.apiKey ?? null,
                    });

                    setDealerIds((dealerIds) => {
                        const updatedDealerIds = [...dealerIds];
                        agency.dealers.forEach((dealer) => {
                            updatedDealerIds.push(dealer.id as number);
                        });
                        return updatedDealerIds;
                    });

                    // Separate DSP entities by platform
                    const amazonIds: number[] = [];
                    const dv360Ids: number[] = [];

                    agency.dspEntities.forEach((dspEntity) => {
                        if (dspEntity.platform.slug === 'amazon') {
                            amazonIds.push(dspEntity.id);
                        } else if (dspEntity.platform.slug === 'dv360') {
                            dv360Ids.push(dspEntity.id);
                        }
                    });

                    setAmazonDspEntityIds(amazonIds);
                    setDv360EntityIds(dv360Ids);

                    const sasAccountIds: number[] = [];
                    agency.sasAccounts.forEach((sasAccount) => {
                        sasAccountIds.push(sasAccount.id);
                    });
                    setSasAccountIds(sasAccountIds);
                })
                .catch(() => {});
            ApiService.getAgencyExportSetting(id).then((response) => {
                const exportSetting = response.data;
                if (exportSetting.id && exportSetting.sources) {
                    setSelectedReports(new Set(exportSetting.sources.split(',').filter(Boolean)));
                }
                if (exportSetting.id && exportSetting.sourcesOndemand) {
                    setSelectedOnDemandReports(new Set(exportSetting.sourcesOndemand.split(',').filter(Boolean)));
                }
                if (exportSetting.id && exportSetting.platforms) {
                    setSelectedScheduledPlatforms(
                        new Set(exportSetting.platforms.split(',').filter(Boolean) as PlatformKey[])
                    );
                }
                if (exportSetting.id && exportSetting.platformsOndemand) {
                    setSelectedOnDemandPlatforms(
                        new Set(exportSetting.platformsOndemand.split(',').filter(Boolean) as PlatformKey[])
                    );
                }
            });
        } else {
            ApiService.getAgencyAvailableDealers(-1).then((response) => {
                setDealers(response.data);
            });
        }

        // Fetch and separate entities by platform
        ApiService.getDspEntities().then((response) => {
            const amazonEntities: DspEntity[] = [];
            const dv360EntitiesList: DspEntity[] = [];

            response.data.forEach((entity) => {
                if (entity.platform.slug === 'amazon') {
                    amazonEntities.push(entity);
                } else if (entity.platform.slug === 'dv360') {
                    dv360EntitiesList.push(entity);
                }
            });

            setAmazonDspEntities(amazonEntities);
            setDv360Entities(dv360EntitiesList);
        });

        ApiService.getSasAccounts().then((response) => {
            setSasAccounts(response.data);
        });
    }, [id]);

    useEffect(() => {
        let selectedDealers = dealers.filter((d: Dealer) => dealerIds.includes(d.id ? d.id : -1));
        setSelectedDealers(selectedDealers);
    }, [dealerIds, dealers]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            showInfoMessage('error', 'Only .csv files are allowed');
            event.target.value = '';
            return;
        }

        setUploadedFileName(file?.name);

        if (file) {
            const formData = new FormData();
            formData.append('bulkShellCampaign', file);

            ApiService.uploadShellCampaignFile(formData, agency.id, 'update')
                .then((response) => {
                    if (response.status === 200) {
                        fetchData();
                        showInfoMessage('success', JSON.stringify(response.data));
                    }
                })
                .catch((error) => {
                    setUploadedFileName(null);
                    event.target.value = '';
                    if (error.response.status === 422) {
                        fetchData();
                        showInfoMessage('warning', JSON.stringify(error.response?.data));
                    } else {
                        showInfoMessage('error', JSON.stringify(error.response?.data));
                    }
                });
        }
    };

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        // Build dealers array
        agency.dealers = [];
        dealerIds.forEach((id) => {
            let dealer = dealers.find((dealer) => dealer.id === id);
            if (dealer) {
                agency.dealers.push(dealer);
            }
        });

        // Build ALL DSP entities array (both Amazon DSP and DV360) with complete entity objects
        const allDspEntities: DspEntity[] = [];

        // Add Amazon DSP entities
        amazonDspEntityIds.forEach((id) => {
            const entity = amazonDspEntities.find((e) => e.id === id);
            if (entity) {
                allDspEntities.push(entity);
            }
        });

        // Add DV360 entities
        dv360EntityIds.forEach((id) => {
            const entity = dv360Entities.find((e) => e.id === id);
            if (entity) {
                allDspEntities.push(entity);
            }
        });

        agency.dspEntities = allDspEntities;

        // Build SAS accounts array
        agency.sasAccounts = [];
        sasAccountIds.forEach((id) => {
            agency.sasAccounts.push({ id: id, accountName: '' });
        });

        if (agency.adServingEnabled && !getSourceFolderSuffix()) {
            setSourceFolderError('Video Source Folder is required when Ad Serving is enabled.');
            return;
        }
        if (agency.adServingEnabled && !getImageSourceFolderSuffix()) {
            setImageSourceFolderError('Image Source Folder is required when Ad Serving is enabled.');
            return;
        }
        if (
            agency.adServingEnabled &&
            getSourceFolderSuffix() &&
            getSourceFolderSuffix() === getImageSourceFolderSuffix()
        ) {
            setSourceFolderError('Video Source Folder and Image Source Folder cannot be the same.');
            setImageSourceFolderError('Video Source Folder and Image Source Folder cannot be the same.');
            return;
        }

        ApiService.updateAgency(agency)
            .then((response) => {
                setAgency(response.data);
                showInfoMessage('success', 'Agency has been updated.');
            })
            .catch((error) => {
                if (error?.response?.status === 400 && error?.response?.data) {
                    const msg =
                        typeof error.response.data === 'string' ? error.response.data : error.response.data.message;
                    if (msg && msg.includes('Image Source Folder')) {
                        setImageSourceFolderError(msg);
                    } else if (msg && msg.includes('Video Source Folder')) {
                        setSourceFolderError(msg);
                    } else if (msg && msg.includes('Source Folder')) {
                        setSourceFolderError(msg);
                    } else {
                        showInfoMessage('error', msg || 'Failed to update agency.');
                    }
                }
            });
    }

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const field = event.target.name;
        if (
            [
                'activeStatus',
                'activeCampaigns',
                'activeReporting',
                'activeQuickSightReporting',
                'activeAttribution',
                'activeData',
                'activeApiDocs',
                'activeAdvertisers',
                'activeCrmUpload',
                'activeInsights',
                'activeAdServing',
                'creativeStudioIsActive',
                'accessCreativeStudio',
                'activeOem',
                'apiIncludeCostMetrics',
                'needsPolkAudiences',
                'polkSalesIsActive',
                'tfUserIsActive',
                'prioritizeReporting',
                'whiteLabelPortalEnabled',
                'activeApiDocs',
                'adServingEnabled',
            ].includes(field)
        ) {
            setAgency({
                ...agency,
                [field]: event.target.checked,
            });
        } else {
            setAgency({
                ...agency,
                [field]: event.target.value,
            });
        }
    }

    // Each field's folder segment is taken from its OWN stored path so the value
    // round-trips exactly (prefix + suffix === stored path) and is never re-appended
    // to the prefix on save. A folder already set (e.g. via DB, like "strongauto") is
    // preserved; when a field has no path yet it falls back to the other field's
    // stored folder, then to the normalized agency name for brand-new setups.
    // COGPLAT-2855.
    const normalizedAgencyName = normalizeAgencyFolderName(agency.name);
    const storedFolderName =
        extractFolderName(agency.sourceFolderPath) ?? extractFolderName(agency.imageSourceFolderPath);
    const videoFolderName = extractFolderName(agency.sourceFolderPath) ?? storedFolderName ?? normalizedAgencyName;
    const imageFolderName = extractFolderName(agency.imageSourceFolderPath) ?? storedFolderName ?? normalizedAgencyName;
    const VIDEO_FOLDER_PREFIX = `${AD_SERVING_PREFIX}${videoFolderName}/`;
    const IMAGE_FOLDER_PREFIX = `${AD_SERVING_PREFIX}${imageFolderName}/`;

    function handleSourceFolderChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSourceFolderError(null);
        setImageSourceFolderError(null);
        if (value === '' || value == null) {
            setAgency({ ...agency, sourceFolderPath: null });
        } else {
            setAgency({ ...agency, sourceFolderPath: VIDEO_FOLDER_PREFIX + value });
        }
    }

    function getSourceFolderSuffix(): string {
        return extractSuffix(agency.sourceFolderPath, videoFolderName);
    }

    function handleImageSourceFolderChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setImageSourceFolderError(null);
        setSourceFolderError(null);
        if (value === '' || value == null) {
            setAgency({ ...agency, imageSourceFolderPath: null });
        } else {
            setAgency({ ...agency, imageSourceFolderPath: IMAGE_FOLDER_PREFIX + value });
        }
    }

    function getImageSourceFolderSuffix(): string {
        return extractSuffix(agency.imageSourceFolderPath, imageFolderName);
    }

    function onFileChange(fileType: string, event: any) {
        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('fileType', fileType);
        formData.append('file', file, file.name);
        ApiService.uploadAgencyImage(formData)
            .then((response) => {
                setAgency({ ...agency, [fileType]: response.data });
            })
            .catch(() => {});
    }

    function handleDealerChange(event: ChangeEvent<HTMLInputElement>) {
        if (Array.isArray(event.target.value)) {
            setDealerIds(event.target.value);
        }
    }

    function renderSelectedDealers(value: unknown): ReactNode {
        let selected: string[] = [];
        let values = value as number[];
        values.forEach((id) => {
            let dealer = dealers.find((dealer) => dealer.id === id);
            if (dealer) {
                selected.push(dealer.dealerName);
            }
        });
        return <span>{selected.join(', ')}</span>;
    }

    function handleSasAccountChange(event: ChangeEvent<HTMLInputElement>) {
        if (Array.isArray(event.target.value)) {
            setSasAccountIds(event.target.value);
        }
    }

    const handleIsTermAccepted = useCallback(
        (event: any | ChangeEvent): void => {
            setIsTermAccepted(event?.target?.checked);

            if (!event?.target?.checked) {
                setAgency({
                    ...agency,
                    polkSalesIsActive: event.target.checked,
                });
            }
        },
        [agency]
    );

    function renderSelectedSasAccounts(value: unknown): ReactNode {
        let selected: string[] = [];
        let values = value as number[];
        values.forEach((id) => {
            let sasAccount = sasAccounts.find((sasAccount) => sasAccount.id === id);
            if (sasAccount) {
                selected.push(sasAccount.accountName);
            }
        });
        return <span>{selected.join(', ')}</span>;
    }

    function generateApiKey() {
        const guid = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        setApiKey(guid);
    }

    function setApiKey(value: string | null) {
        if (!value) {
            setAgency({ ...agency, apiKey: value, activeApiDocs: false, apiIncludeCostMetrics: false });
        } else {
            setAgency({ ...agency, apiKey: value });
        }
    }

    function modifyAgency(update: Partial<Agency>) {
        setAgency({ ...agency, ...update });
    }

    const handleAgencyDetailsUpdate: AgencyUpdateCallback = (name, update) => {
        modifyAgency(update);
    };

    function selectTab(_event: SyntheticEvent, value: TabSlug) {
        setSelectedTab(value);

        if (params.id != null) {
            navigate(`/agencies/${params.id}/${value}`);
        }
    }

    let action = agency.apiKey ? (
        <Tooltip title="Remove API Key">
            <IconButton onClick={() => setApiKey('')}>
                <CancelOutlined />
            </IconButton>
        </Tooltip>
    ) : (
        <Tooltip title="Generate API Key">
            <IconButton onClick={generateApiKey}>
                <KeyOutlined />
            </IconButton>
        </Tooltip>
    );

    function renderExportOptions(): ReactNode {
        const itemList = [];
        for (let i = 1; i <= 12; i++) {
            itemList.push(<MenuItem value={i}>{i} Month</MenuItem>);
        }
        return itemList;
    }

    const fetchData = async () => {
        const transformedSortModel = transformSortModelForBackend(sortModel);
        const response: AxiosPageableResponse<ShellCampaign> = await ApiService.getBulkUpdateCampaigns(
            page,
            pageSize,
            transformedSortModel,
            id
        );
        if (response.status === 200) {
            setList(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    const handleDelete = async (campaignId: number) => {
        try {
            await ApiService.deleteCampaign(campaignId);
            await fetchData();
            showInfoMessage('success', 'Campaign deleted successfully.');
        } catch (error: any) {
            showInfoMessage('error', 'Failed to delete campaign.');
        }
    };

    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [list, setList] = useState<ShellCampaign[] | []>([]);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);

    /**
     * another hook that runs on mount
     */
    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id, page, pageSize, sortModel]);

    const handleShellCampaignChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event?.target?.value);
        if (pageValueArray.includes(value)) {
            setPageSize(value);
            setPage(0);
        } else {
            setPageSize(defaultPageSize);
            setPage(0);
        }
    };

    // ====================Bulk Upload Table============================
    const [editIds, setEditIds] = useState<number[]>([]);
    const [advertiser, setAdvertiser] = useState<string>('');
    const [dspAdvertiserID, setDspAdvertiserID] = useState<string>('');
    const [platform, setPlatform] = useState<string>('');
    const [oemProgram, setOemProgram] = useState<string>('');
    const [poNumber, setPoNumber] = useState<string>('');
    const [campaignName, setCampaignName] = useState<string>('');
    const [dspCampaignID, setDspCampaignID] = useState<string>('');
    const [budget, setBudget] = useState<number | null>(null);

    const handleOpen = () => {
        const selectedRows = list.filter((row) => editIds.includes(row.id));
        const hasSyncedRows = selectedRows.some((row) => row.dspCampaignSyncedDate);

        if (hasSyncedRows) {
            showInfoMessage(
                'warning',
                'Cannot update campaigns that have been synced with DSP. Please deselect synced campaigns and try again.'
            );
            return;
        }

        resetForm();
        setOpen(true);
    };

    function bulkChangeSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const payload: any = {};

        if (advertiser) payload.advertiser = advertiser;
        if (dspAdvertiserID) payload.dspAdvertiserID = dspAdvertiserID;
        if (platform) payload.platform = platform;
        if (oemProgram) payload.oemProgram = oemProgram;
        if (poNumber) payload.poNumber = poNumber;
        if (dspCampaignID) payload.dspCampaignID = dspCampaignID;
        if (campaignName) payload.campaignName = campaignName;
        if (budget !== null && budget !== undefined && !isNaN(budget)) payload.budget = budget;

        ApiService.updateBulkCampaigns({
            ids: editIds,
            shellCampaignDto: payload,
        }).then(() => {
            fetchData();
            showInfoMessage('success', 'Successfully updated campaigns.');
        });

        setEditIds([]);
        handleClose();
    }

    const editModalStyles = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };
    const handleClose = () => {
        resetForm();
        setOpen(false);
    };
    const resetForm = () => {
        setAdvertiser('');
        setDspAdvertiserID('');
        setPlatform('');
        setBudget(null);
        setPoNumber('');
        setDspCampaignID('');
        setCampaignName('');
    };
    const [open, setOpen] = useState(false);
    const isRowSelectable = (params: any) => {
        return !params.row.isSyncing;
    };
    const columns: GridColDef[] = [
        {
            flex: 0.8,
            minWidth: 80,
            maxWidth: 100,
            field: 'id',
            headerName: 'ID',
            display: 'flex',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <MuiLink
                    component={Link}
                    to={`/campaign/${params.row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {params.value}
                </MuiLink>
            ),
        },
        {
            flex: 1.5,
            minWidth: 150,
            field: 'advertiser',
            headerName: 'Advertiser',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.8,
            minWidth: 160,
            field: 'dspAdvertiserID',
            headerName: 'DSP Advertiser ID',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 120,
            field: 'platform',
            headerName: 'Platform',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.4,
            minWidth: 140,
            field: 'oemProgram',
            headerName: 'OEM Program',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.3,
            minWidth: 130,
            field: 'poNumber',
            headerName: 'PO Number',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.8,
            minWidth: 160,
            field: 'dspCampaignID',
            headerName: 'DSP Campaign ID',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 2,
            minWidth: 180,
            field: 'campaignName',
            headerName: 'Campaign Name',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontWeight: '500',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 2,
            minWidth: 180,
            field: 'mediaType',
            headerName: 'Campaign Media Type',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontWeight: '500',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 2,
            minWidth: 180,
            field: 'strategyType',
            headerName: 'Campaign Strategy',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontWeight: '500',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 2,
            minWidth: 180,
            field: 'campaignType',
            headerName: 'Campaign Type',
            editable: false,
            display: 'flex',
            renderCell: (params) => (
                <div
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '8px 4px',
                        fontWeight: '500',
                    }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            flex: 1.2,
            minWidth: 120,
            field: 'budget',
            headerName: 'Budget',
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            lineHeight: '1.2',
                            padding: '8px 4px',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            fontWeight: '500',
                        }}
                    >
                        {Utils.formatCurrency(params.value, 0)}
                    </div>
                );
            },
            editable: false,
            display: 'flex',
            headerAlign: 'center',
            align: 'center',
        },
        {
            flex: 1.5,
            minWidth: 140,
            field: 'dspCampaignSyncedDate',
            headerName: 'Last DSP Sync',
            type: 'string',
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            lineHeight: '1.2',
                            padding: '8px 4px',
                            fontFamily: 'monospace',
                            fontSize: '0.85em',
                        }}
                    >
                        {params.value ? Utils.toLocalTime(params.value).toLocaleString() : null}
                    </div>
                );
            },
        },
        {
            flex: 0,
            width: 90,
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                if (params.row.isSyncing) {
                    return (
                        <Tooltip title="Campaign is syncing...">
                            <SyncIcon
                                sx={{
                                    fontSize: 20,
                                    color: 'primary.main',
                                    animation: 'spin 2s linear infinite',
                                    '@keyframes spin': {
                                        '0%': {
                                            transform: 'rotate(0deg)',
                                        },
                                        '100%': {
                                            transform: 'rotate(360deg)',
                                        },
                                    },
                                }}
                            />
                        </Tooltip>
                    );
                } else if (params.row.dspCampaignSyncedDate) {
                    return null;
                } else {
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(params.row.id);
                            }}
                            title="Delete Campaign"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    );
                }
            },
        },
    ];

    // ==========================================================
    // ======================================== RENDERED OUTPUT
    // ==========================================================

    return (
        <>
            <InfoMessage {...infoMessage} />
            <Block>
                <Column gap={1}>
                    <AppBreadcrumbs
                        items={[{ label: 'Agencies', href: '/agencies' }, { label: agency.name || '...' }]}
                    />
                </Column>
            </Block>

            {/*
             * Top tabs banner
             *
             */}
            <Block disableGutters>
                <TabsContainer>
                    <Tabs value={selectedTab} onChange={selectTab}>
                        <Tab value={TABS['agency-settings'].slug} label={TABS['agency-settings'].label} />
                        <Tab value={TABS.advertisers.slug} label={TABS.advertisers.label} />
                        {userContext.isAdmin() && (
                            <Tab value={TABS['access-permissions'].slug} label={TABS['access-permissions'].label} />
                        )}
                        {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) && (
                            <Tab value={TABS['dsp-integration-map'].slug} label={TABS['dsp-integration-map'].label} />
                        )}
                        <Tab value={TABS.fees.slug} label={TABS.fees.label} />
                        <Tab value={TABS['campaign-parameters'].slug} label={TABS['campaign-parameters'].label} />
                        <Tab value={TABS['first-party-data'].slug} label={TABS['first-party-data'].label} />
                        <Tab value={TABS['reporting-exports'].slug} label={TABS['reporting-exports'].label} />
                    </Tabs>
                </TabsContainer>
            </Block>

            <Block>
                <form onSubmit={handleSubmit}>
                    {/*
                     * Agency Settings tab
                     *
                     */}
                    <TabPanel value={selectedTab} index={TABS['agency-settings'].slug}>
                        <Grid container spacing={3}>
                            {/* Name */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                            >
                                <Grid size={5}>
                                    <TextField
                                        name="name"
                                        label="Agency Name"
                                        value={agency.name}
                                        required={true}
                                        fullWidth={true}
                                        size="small"
                                        variant="outlined"
                                        onChange={handleChange}
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                        }}
                                    />
                                </Grid>
                            </AccessGuard>

                            {/* Status */}
                            <AccessGuard>
                                <Grid size={2} sx={{ textAlign: 'right', pr: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Tooltip title="Enable or disable this agency; disabling it will remove the agency as a selectable option in Advertiser Assignment, Pacing Report, etc.">
                                                <Switch
                                                    checked={agency.activeStatus}
                                                    onChange={handleChange}
                                                    name={'activeStatus'}
                                                />
                                            </Tooltip>
                                        }
                                        label={
                                            <ActiveStatus
                                                activeStatus={agency.activeStatus}
                                                activeText="Active"
                                                inactiveText="Inactive"
                                            />
                                        }
                                        labelPlacement="start"
                                        data-testId="active-status-toggle"
                                    />
                                </Grid>
                            </AccessGuard>

                            {/* CRM Data Notifications */}
                            <Grid size={7}>
                                <TextField
                                    name="crmDataNotifications"
                                    label="CRM Data Notifications"
                                    value={agency.crmDataNotifications}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="start">
                                                    <Tooltip title="Send CRM Data notifications to these recipients instead of the Advertiser.  Separate multiple email addresses with a comma.">
                                                        <InfoOutlined color="primary" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        },

                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>

                            {/* Logo URL */}
                            <Grid size={7}>
                                <TextField
                                    name="logoUrl"
                                    fullWidth={true}
                                    size="small"
                                    label="Logo URL"
                                    onChange={handleChange}
                                    variant="outlined"
                                    value={agency.logoUrl}
                                    helperText="Preferred horizontal format similar to size = 800px wide by 100 px tall"
                                    required={agency.whiteLabelPortalEnabled}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <InputLabel htmlFor="btn-uploadLogo">
                                                        <input
                                                            id="btn-uploadLogo"
                                                            name="btn-uploadLogo"
                                                            style={{
                                                                display: 'none',
                                                            }}
                                                            type="file"
                                                            accept=".png,.jpg"
                                                            onChange={(event) => onFileChange('logoUrl', event)}
                                                        />
                                                        <Tooltip title="Upload Agency Logo image">
                                                            <CloudUploadOutlined
                                                                color="primary"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    </InputLabel>
                                                </InputAdornment>
                                            ),
                                        },

                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>

                            {/* Logo URL - Preview */}
                            <Grid size={3}>
                                <a href={agency.logoUrl} target="new">
                                    <img style={{ height: '40px' }} src={agency.logoUrl} alt="" />
                                </a>
                            </Grid>

                            {/* Dashboard Selector */}
                            <Grid size={7}>
                                <QuickSightDashboardSelector
                                    label="Select a Dashboard"
                                    dashboardId={agency.dashboardId}
                                    onChange={(value) =>
                                        setAgency({
                                            ...agency,
                                            dashboardId: value,
                                        })
                                    }
                                />
                            </Grid>

                            {/* AAS Accounts */}
                            <Grid size={7}>
                                <TextField
                                    fullWidth
                                    label="Available AAS Accounts"
                                    name="sasAccounts"
                                    value={sasAccountIds}
                                    variant="outlined"
                                    required={true}
                                    size="small"
                                    select
                                    onChange={handleSasAccountChange}
                                    slotProps={{
                                        select: {
                                            multiple: true,
                                            renderValue: renderSelectedSasAccounts,
                                        },

                                        inputLabel: { shrink: true },
                                    }}
                                >
                                    {sasAccounts.map((sasAccount) => {
                                        return (
                                            <MenuItem key={sasAccount.id} value={sasAccount.id}>
                                                <Checkbox
                                                    checked={
                                                        sasAccountIds.find((d) => d === sasAccount.id) !== undefined
                                                    }
                                                />
                                                {sasAccount.accountName}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            {/* Amazon DSP Timezones */}
                            <Grid size={7}>
                                <FormControl style={{ width: '100%' }}>
                                    <TextField
                                        name="timezone"
                                        label="Amazon DSP Timezone"
                                        select
                                        size="small"
                                        variant="outlined"
                                        value={agency.timezone || ''}
                                        onChange={handleChange}
                                        slotProps={{
                                            select: { native: false },
                                            inputLabel: { shrink: true },
                                        }}
                                    >
                                        {timezones.map((timezone) => {
                                            return (
                                                <MenuItem key={timezone} value={timezone}>
                                                    {timezone.replaceAll('_', ' ')}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </FormControl>
                            </Grid>

                            {/* Creative Studio API */}
                            <Grid size={7}>
                                <Grid>
                                    <FormSection>
                                        <Typography variant="h6">Creative Studio API</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={agency.creativeStudioIsActive}
                                                    name="creativeStudioIsActive"
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={<ActiveStatus activeStatus={agency.creativeStudioIsActive} />}
                                        />
                                        {agency.creativeStudioIsActive && (
                                            <>Creative Studio Agency ID: {agency.creativeStudioUserId}</>
                                        )}
                                    </FormSection>
                                </Grid>
                            </Grid>

                            {/* Polk Audiences */}
                            <Grid size={7}>
                                <Grid>
                                    <FormSection>
                                        <Typography variant="h6">Polk Audiences</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={agency.needsPolkAudiences}
                                                    name="needsPolkAudiences"
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={<ActiveStatus activeStatus={agency.needsPolkAudiences} />}
                                        />
                                    </FormSection>
                                </Grid>
                            </Grid>

                            {/* Polk Sales */}
                            <Grid size={7}>
                                <Grid>
                                    <FormSection>
                                        <Typography variant="h6">Polk Sales</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={agency.polkSalesIsActive}
                                                    name="polkSalesIsActive"
                                                    onChange={handleChange}
                                                    disabled={!isTermAccepted && !agency.polkSalesIsActive}
                                                />
                                            }
                                            label={<ActiveStatus activeStatus={agency.polkSalesIsActive} />}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onChange={handleIsTermAccepted}
                                                    defaultChecked={agency.polkSalesIsActive}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    By using Cognition's platform you are agreeing to our{' '}
                                                    <MuiLink
                                                        href="//www.cognitionads.com/terms-of-service"
                                                        target="new"
                                                        color="primary"
                                                    >
                                                        terms of service
                                                    </MuiLink>
                                                    .
                                                </Typography>
                                            }
                                            sx={{ mx: 0, px: 2 }}
                                        />
                                    </FormSection>
                                </Grid>
                            </Grid>

                            {/* Gateway API */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                            >
                                <Grid size={7}>
                                    <Grid>
                                        <FormSection>
                                            <AgencyGatewayAPI
                                                agency={agency}
                                                onAgencyUpdate={handleAgencyDetailsUpdate}
                                            />
                                        </FormSection>
                                    </Grid>
                                </Grid>
                            </AccessGuard>

                            {/* HAT */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                            >
                                <Grid size={7}>
                                    <Grid>
                                        <FormSection>
                                            <Typography variant="h6">HAT</Typography>
                                            <Row justifyContent="space-between">
                                                <FormControlLabel
                                                    control={
                                                        <Tooltip title="HAT User (Trialfire user)">
                                                            <Switch
                                                                checked={agency.tfUserIsActive}
                                                                onChange={handleChange}
                                                                name={'tfUserIsActive'}
                                                            />
                                                        </Tooltip>
                                                    }
                                                    label={<ActiveStatus activeStatus={agency.tfUserIsActive} />}
                                                />
                                                {agency.tfUserIsActive && (
                                                    <Column gap={1} sx={{ minWidth: 240, width: 'auto' }}>
                                                        <Row gap={0.5}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Customer ID:
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                {agency.tfCustomerId || '-'}
                                                            </Typography>
                                                        </Row>
                                                        <Row gap={0.5}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Username:
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                {agency.tfUserEmail || '-'}
                                                            </Typography>
                                                        </Row>
                                                        <Row gap={0.5}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Password:
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                {agency.tfPassword || '-'}
                                                            </Typography>
                                                        </Row>
                                                    </Column>
                                                )}
                                            </Row>
                                        </FormSection>
                                    </Grid>
                                </Grid>
                            </AccessGuard>

                            {/* OEM SLA */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                            >
                                <Grid size={7}>
                                    <Grid>
                                        <FormSection>
                                            <Typography variant="h6">
                                                OEM SLA (Prioritized Attribution & Reporting)
                                            </Typography>
                                            <Row justifyContent="space-between">
                                                <FormControlLabel
                                                    control={
                                                        <Tooltip title="When enabled this prioritizes the Agency's import and export of all DSP Advertiser reporting and Attribution reporting workflows to meet OEM Program SLA requirements.">
                                                            <Switch
                                                                checked={agency.prioritizeReporting}
                                                                onChange={handleChange}
                                                                name={'prioritizeReporting'}
                                                            />
                                                        </Tooltip>
                                                    }
                                                    label={<ActiveStatus activeStatus={agency.prioritizeReporting} />}
                                                />
                                            </Row>
                                        </FormSection>
                                    </Grid>
                                </Grid>
                            </AccessGuard>

                            {/* OEM & OEM Program Settings */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin()}
                            >
                                <Grid
                                    size={7}
                                    sx={{
                                        bgcolor: 'grey.100',
                                        border: 1,
                                        borderColor: 'grey.100',
                                        px: 1,
                                        py: 1,
                                        borderRadius: 1,
                                        mb: 2,
                                        mt: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {/* HEADER */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            px: 2,
                                            pt: 0.5,
                                            pb: 1,
                                        }}
                                    >
                                        {/* icon and label */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'left',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <EmojiTransportationOutlined fontSize="small" sx={{ color: 'black' }} />

                                            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                                OEM & OEM Program Settings
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* CONTENT --- oem settings */}
                                    <Box sx={{ p: 4, backgroundColor: 'white', borderRadius: 1 }}>
                                        <AgencyOemProgramFieldset
                                            agency={agency}
                                            setAgency={setAgency}
                                            handleChange={handleChange}
                                        />
                                    </Box>
                                </Grid>
                            </AccessGuard>

                            {/* White Label */}
                            <AccessGuard
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                            >
                                <Grid size={7}>
                                    <Box
                                        sx={{
                                            bgcolor: 'grey.100',
                                            border: 1,
                                            borderColor: 'grey.100',
                                            px: 1,
                                            py: 1,
                                            borderRadius: 1,
                                            mb: 2,
                                            mt: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {/* HEADER --- icon, label, and toggle */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                px: 2,
                                                pt: 0.5,
                                                pb: 1,
                                            }}
                                        >
                                            {/* icon and label */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'left',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <LocalOfferOutlined fontSize="small" sx={{ color: 'black' }} />

                                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                                    White Label Settings
                                                </Typography>
                                            </Box>

                                            {/* toggle */}
                                            <Tooltip
                                                title={
                                                    <>
                                                        Enable to provide this agency their own portal to the Cognition
                                                        UI.
                                                        <br /> <br />
                                                        <strong>NOTE:</strong> This will require a new DNS record to be
                                                        added to AWS.
                                                    </>
                                                }
                                            >
                                                <Switch
                                                    checked={agency.whiteLabelPortalEnabled} // TODO: change once db is updated
                                                    onChange={handleChange}
                                                    name={'whiteLabelPortalEnabled'}
                                                />
                                            </Tooltip>
                                        </Box>

                                        {/* Portal On --- Show Settings */}
                                        <Collapse in={agency.whiteLabelPortalEnabled}>
                                            <Column
                                                sx={{
                                                    p: '32px 32px 20px 32px',
                                                    backgroundColor: 'white',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Row>
                                                    {/* Portal Hostname */}
                                                    <TextField
                                                        name="hostname"
                                                        label="Agency Portal Hostname"
                                                        value={agency.hostname}
                                                        required={agency.whiteLabelPortalEnabled}
                                                        fullWidth={true}
                                                        size="small"
                                                        variant="outlined"
                                                        onChange={handleChange}
                                                        slotProps={{
                                                            inputLabel: { shrink: true },
                                                            htmlInput: { maxLength: 80 },
                                                        }}
                                                    />

                                                    {/* Support Email */}
                                                    <Tooltip title="This is the email address that will appear in emails as the contact for Customer Support inquiries.">
                                                        <TextField
                                                            name="supportEmail"
                                                            label="Support Email"
                                                            value={agency.supportEmail}
                                                            required={agency.whiteLabelPortalEnabled}
                                                            fullWidth={true}
                                                            size="small"
                                                            variant="outlined"
                                                            onChange={handleChange}
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                                htmlInput: { maxLength: 100 },
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Row>

                                                <Row>
                                                    {/* Favicon */}
                                                    <Grid size={10}>
                                                        <TextField
                                                            name="iconUrl"
                                                            fullWidth={true}
                                                            size="small"
                                                            label="Favorite Icon"
                                                            onChange={handleChange}
                                                            variant="outlined"
                                                            value={agency.iconUrl}
                                                            helperText="Preferred size = 32x32px"
                                                            required={agency.whiteLabelPortalEnabled}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <InputLabel htmlFor="btn-uploadBadge">
                                                                                <input
                                                                                    id="btn-uploadBadge"
                                                                                    name="btn-uploadBadge"
                                                                                    style={{
                                                                                        display: 'none',
                                                                                    }}
                                                                                    type="file"
                                                                                    accept=".png,.jpg"
                                                                                    onChange={(event) =>
                                                                                        onFileChange('iconUrl', event)
                                                                                    }
                                                                                />
                                                                                <Tooltip title="Upload Favorite Icon">
                                                                                    <CloudUploadOutlined
                                                                                        color="primary"
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            </InputLabel>
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                inputLabel: { shrink: true },
                                                            }}
                                                        />
                                                    </Grid>

                                                    {/* Favicon - Preview */}
                                                    <Grid
                                                        size={2}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'flex-start',
                                                        }}
                                                    >
                                                        <a href={agency.iconUrl} target="new">
                                                            <img
                                                                style={{ height: '40px' }}
                                                                src={agency.iconUrl}
                                                                alt="Agency Favicon"
                                                            />
                                                        </a>
                                                    </Grid>
                                                </Row>

                                                {/* Terms of Service */}
                                                <TextField
                                                    name="termsOfService"
                                                    label="Terms of Service URL"
                                                    value={agency.termsOfService}
                                                    required={agency.whiteLabelPortalEnabled}
                                                    fullWidth={true}
                                                    size="small"
                                                    variant="outlined"
                                                    onChange={handleChange}
                                                    slotProps={{
                                                        inputLabel: { shrink: true },
                                                    }}
                                                />

                                                {/* Custom Sender Email Section */}
                                                <Divider sx={{ my: 2 }} />
                                                <SenderEmailSection
                                                    agency={agency}
                                                    setAgency={setAgency}
                                                    showInfoMessage={showInfoMessage}
                                                />
                                            </Column>
                                        </Collapse>
                                    </Box>
                                </Grid>
                            </AccessGuard>

                            {/* Ad Serving Settings */}
                            <AccessGuard accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <Grid size={7}>
                                    <Box
                                        sx={{
                                            bgcolor: 'grey.100',
                                            border: 1,
                                            borderColor: 'grey.100',
                                            px: 1,
                                            py: 1,
                                            borderRadius: 1,
                                            mb: 2,
                                            mt: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {/* HEADER --- icon, label, and toggle */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                px: 2,
                                                pt: 0.5,
                                                pb: 1,
                                            }}
                                        >
                                            {/* icon and label */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'left',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <RssFeedOutlined fontSize="small" sx={{ color: 'black' }} />

                                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                                    Ad Serving Settings
                                                </Typography>
                                            </Box>

                                            {/* toggle */}
                                            <Tooltip title="Enable Ad Serving for this agency. When enabled, both Video and Image Source Folder paths must be configured for ad tag creation.">
                                                <span>
                                                    <Switch
                                                        checked={agency.adServingEnabled}
                                                        onChange={handleChange}
                                                        name="adServingEnabled"
                                                        disabled={!userContext.isAdmin() && !userContext.isOpsAdmin()}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Box>

                                        {/* Ad Serving On --- Show Source Folder */}
                                        <Collapse in={agency.adServingEnabled}>
                                            <Column
                                                sx={{
                                                    p: '32px 32px 20px 32px',
                                                    backgroundColor: 'white',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                {userContext.isAdmin() || userContext.isOpsAdmin() ? (
                                                    <>
                                                        {/* Video Source Folder label with tooltip */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography variant="body2" fontWeight={500}>
                                                                Video Source Folder
                                                            </Typography>
                                                            <Tooltip title="This is the S3/FTP path where video assets are uploaded by the agency for VAST Video ad tag generation.">
                                                                <InfoOutlined
                                                                    fontSize="small"
                                                                    sx={{ color: 'text.secondary', fontSize: '16px' }}
                                                                />
                                                            </Tooltip>
                                                        </Box>

                                                        {/* Video Source Folder text field with locked prefix */}
                                                        <TextField
                                                            label="Video Source Folder"
                                                            size="small"
                                                            variant="outlined"
                                                            fullWidth={true}
                                                            value={getSourceFolderSuffix()}
                                                            onChange={handleSourceFolderChange}
                                                            placeholder="dynamicvideos"
                                                            error={Boolean(sourceFolderError)}
                                                            helperText={sourceFolderError}
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start" sx={{ mr: 0 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.disabled"
                                                                                sx={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                cd-ftp-files/incoming/
                                                                            </Typography>
                                                                            <Tooltip title="This must match the agency's S3 folder name">
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="primary"
                                                                                    sx={{
                                                                                        whiteSpace: 'nowrap',
                                                                                        fontWeight: 'bold',
                                                                                        cursor: 'help',
                                                                                    }}
                                                                                >
                                                                                    {videoFolderName}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.disabled"
                                                                                sx={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                /
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                            sx={{ mb: 3 }}
                                                        />

                                                        {/* Image Source Folder label with tooltip */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography variant="body2" fontWeight={500}>
                                                                Image Source Folder
                                                            </Typography>
                                                            <Tooltip title="This is the S3/FTP path where image assets are uploaded by the agency for HTML Image ad tag generation.">
                                                                <InfoOutlined
                                                                    fontSize="small"
                                                                    sx={{ color: 'text.secondary', fontSize: '16px' }}
                                                                />
                                                            </Tooltip>
                                                        </Box>

                                                        {/* Image Source Folder text field with locked prefix */}
                                                        <TextField
                                                            label="Image Source Folder"
                                                            size="small"
                                                            variant="outlined"
                                                            fullWidth={true}
                                                            value={getImageSourceFolderSuffix()}
                                                            onChange={handleImageSourceFolderChange}
                                                            placeholder="dynamicimages"
                                                            error={Boolean(imageSourceFolderError)}
                                                            helperText={imageSourceFolderError}
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start" sx={{ mr: 0 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.disabled"
                                                                                sx={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                cd-ftp-files/incoming/
                                                                            </Typography>
                                                                            <Tooltip title="This must match the agency's S3 folder name">
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="primary"
                                                                                    sx={{
                                                                                        whiteSpace: 'nowrap',
                                                                                        fontWeight: 'bold',
                                                                                        cursor: 'help',
                                                                                    }}
                                                                                >
                                                                                    {imageFolderName}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.disabled"
                                                                                sx={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                /
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        {agency.sourceFolderPath || agency.imageSourceFolderPath ? (
                                                            <>
                                                                {/* Agency view — Video Source Folder */}
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        mb: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        Video Source Folder
                                                                    </Typography>
                                                                    <Tooltip title="This is the S3/FTP path where video assets are uploaded by the agency for VAST Video ad tag generation.">
                                                                        <InfoOutlined
                                                                            fontSize="small"
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                                <TextField
                                                                    label="Video Source Folder"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    fullWidth={true}
                                                                    value={agency.sourceFolderPath || ''}
                                                                    disabled={true}
                                                                    slotProps={{
                                                                        inputLabel: { shrink: true },
                                                                    }}
                                                                    sx={{ mb: 3 }}
                                                                />

                                                                {/* Agency view — Image Source Folder */}
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        mb: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        Image Source Folder
                                                                    </Typography>
                                                                    <Tooltip title="This is the S3/FTP path where image assets are uploaded by the agency for HTML Image ad tag generation.">
                                                                        <InfoOutlined
                                                                            fontSize="small"
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                                <TextField
                                                                    label="Image Source Folder"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    fullWidth={true}
                                                                    value={agency.imageSourceFolderPath || ''}
                                                                    disabled={true}
                                                                    slotProps={{
                                                                        inputLabel: { shrink: true },
                                                                    }}
                                                                />
                                                            </>
                                                        ) : (
                                                            /* Agency view — no paths configured */
                                                            <Typography variant="body2" color="text.secondary">
                                                                Email support@cognitionads.com to enable Ad Serving and
                                                                setup your Source Folders for creating ad tags.
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Column>
                                        </Collapse>
                                    </Box>
                                </Grid>
                            </AccessGuard>
                        </Grid>
                    </TabPanel>

                    {/*
                     * Advertisers tab
                     *
                     */}
                    <TabPanel value={selectedTab} index={TABS.advertisers.slug}>
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    md: 12,
                                }}
                            >
                                {dealers.length > 0 && (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Advertisers"
                                            name="dealers"
                                            value={dealerIds}
                                            variant="outlined"
                                            required={true}
                                            size="small"
                                            select
                                            onChange={handleDealerChange}
                                            slotProps={{
                                                select: {
                                                    multiple: true,
                                                    renderValue: renderSelectedDealers,
                                                },
                                            }}
                                        >
                                            {dealers.map((dealer) => {
                                                const isActive = dealer.activeStatus;
                                                return (
                                                    <MenuItem
                                                        key={dealer.id}
                                                        value={dealer.id}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Checkbox
                                                                checked={
                                                                    dealerIds.find((d) => d === dealer.id) !== undefined
                                                                }
                                                            />
                                                            <span>{dealer.dealerName}</span>
                                                        </div>
                                                        <span
                                                            style={{
                                                                fontSize: '12px',
                                                                padding: '2px 8px',
                                                                borderRadius: '12px',
                                                                backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                                                color: isActive ? '#2e7d32' : '#c62828',
                                                                fontWeight: 500,
                                                                marginLeft: '8px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </MenuItem>
                                                );
                                            })}
                                        </TextField>
                                    </>
                                )}
                            </Grid>
                            <Grid
                                size={{
                                    md: 12,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                    <AgencyAdvertiserExportCsvButton
                                        dealers={selectedDealers}
                                        dspAdvertiserNames={dspAdvertiserNames}
                                        agencyId={agency.id}
                                        agencyName={agency.name}
                                    />
                                </Box>
                                <CDGridPro
                                    showToolbar={false}
                                    columns={[
                                        {
                                            field: 'dealerName',
                                            headerName: 'Advertiser',
                                            flex: 1,
                                            renderCell: (params: GridRenderCellParams) => (
                                                <MuiLink
                                                    component={Link}
                                                    to={`/advertisers/${params.row.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {params.value}
                                                </MuiLink>
                                            ),
                                        },
                                        {
                                            field: 'activeStatus',
                                            headerName: 'Status',
                                            width: 120,
                                            renderCell: (params: GridRenderCellParams) => {
                                                const isActive = params.value === true;
                                                return (
                                                    <Chip
                                                        label={isActive ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                                            color: isActive ? '#2e7d32' : '#c62828',
                                                            fontWeight: 500,
                                                            fontSize: '12px',
                                                        }}
                                                    />
                                                );
                                            },
                                        },
                                        {
                                            field: 'url',
                                            headerName: 'Website',
                                            flex: 1,
                                            renderCell: (params: GridRenderCellParams) => {
                                                const url = (params.value as string)?.trim();
                                                if (!url) {
                                                    return null;
                                                }
                                                const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
                                                return (
                                                    <MuiLink href={href} target="_blank" rel="noopener noreferrer">
                                                        {url}
                                                    </MuiLink>
                                                );
                                            },
                                        },
                                        {
                                            field: 'polkDealerName',
                                            headerName: 'Polk Dealer Assigned',
                                            flex: 1,
                                            renderCell: (params: GridRenderCellParams) => params.value || '',
                                        },
                                        {
                                            field: 'hatTrackingStatus',
                                            headerName: 'HAT Tracking Status',
                                            width: 180,
                                            sortable: false,
                                            renderCell: (params: GridRenderCellParams) => (
                                                <HatTrackingStatusCell advertiserId={params.row.id} />
                                            ),
                                        },
                                        {
                                            field: 'dspAdvertiser',
                                            headerName: 'Amazon DSP Advertiser ID',
                                            width: 200,
                                            renderCell: (params: GridRenderCellParams) => params.value || '',
                                        },
                                        {
                                            field: 'dspAdvertiserStatus',
                                            headerName: 'Amazon DSP Advertiser Status',
                                            width: 280,
                                            sortable: false,
                                            renderCell: (params: GridRenderCellParams) => (
                                                <DspAdvertiserStatusCell
                                                    dspAdvertiserId={params.row.dspAdvertiser}
                                                    advertiserName={dspAdvertiserNames?.[params.row.dspAdvertiser]}
                                                    isLoading={isLoadingDspAdvertiserNames}
                                                    dealerName={params.row.dealerName}
                                                />
                                            ),
                                        },
                                        {
                                            field: 'amcInstanceId',
                                            headerName: 'AMC Instance Created',
                                            width: 180,
                                            renderCell: (params: GridRenderCellParams) => {
                                                const created = params.value != null && params.value !== 0;
                                                return (
                                                    <Chip
                                                        label={created ? 'Yes' : 'No'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: created ? '#e8f5e9' : '#f5f5f5',
                                                            color: created ? '#2e7d32' : '#757575',
                                                            fontWeight: 500,
                                                            fontSize: '12px',
                                                        }}
                                                    />
                                                );
                                            },
                                        },
                                    ]}
                                    rows={selectedDealers}
                                />
                            </Grid>

                            {agency?.activeCampaigns && (
                                <Grid size={{ md: 12 }}>
                                    <Divider sx={{ mt: 4, mb: 2 }} />
                                </Grid>
                            )}

                            <Grid size={{ md: 12 }}>
                                {agency?.activeCampaigns && (
                                    <FormSection>
                                        <Typography variant="h6" gutterBottom>
                                            Bulk Shell Campaign Upload
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<UploadFile />}
                                                size="small"
                                                sx={{ minWidth: '120px', padding: '4px 8px', fontSize: '0.75rem' }}
                                            >
                                                Upload File
                                                <input
                                                    type="file"
                                                    accept=".csv,text/csv"
                                                    hidden
                                                    onChange={handleFileUpload}
                                                />
                                            </Button>
                                            {uploadedFileName && (
                                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                    {uploadedFileName}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </FormSection>
                                )}
                            </Grid>
                            <Grid size={{ md: 12 }}>
                                {agency?.activeCampaigns && (
                                    <Column gap={2}>
                                        <Row justifyContent="space-between" alignItems="center">
                                            <Row gap={0} sx={{ width: '100%' }}>
                                                <Typography
                                                    component="span"
                                                    color="text.primary"
                                                    variant="subtitle2"
                                                    fontWeight={500}
                                                >
                                                    <Typography
                                                        component="span"
                                                        color="text.secondary"
                                                        variant="subtitle2"
                                                        fontWeight={500}
                                                    >
                                                        Shell Bulk Upload Results
                                                    </Typography>
                                                </Typography>
                                            </Row>
                                            <Row flex={0} justifyContent="flex-end" spacing={1}>
                                                <FormControl sx={{ minWidth: 120 }} size="small">
                                                    <InputLabel id="demo-select-small-label">Rows Per Page</InputLabel>
                                                    <Select
                                                        labelId="demo-select-small-label"
                                                        id="demo-select-small"
                                                        value={pageSize.toString()}
                                                        label="Rows Per Page"
                                                        onChange={handleShellCampaignChange}
                                                    >
                                                        {pageValueArray?.map((value) => {
                                                            return (
                                                                <MenuItem key={value} value={value}>
                                                                    {value}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormControl>
                                                {editIds?.length >= 1 ? (
                                                    <>
                                                        <Button
                                                            variant="text"
                                                            onClick={handleOpen}
                                                            startIcon={<BorderColorIcon />}
                                                            sx={{ marginTop: '-1%' }}
                                                        >
                                                            Update Campaigns
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </Row>
                                        </Row>

                                        <CDGrid
                                            id="bulkFetchUploadId"
                                            variant="contained"
                                            checkboxSelection
                                            disableRowSelectionOnClick
                                            isRowSelectable={isRowSelectable}
                                            sx={{
                                                height: 'calc(100vh - 400px)',
                                                width: '100%',
                                                '& .MuiDataGrid-main': {
                                                    width: '100%',
                                                },
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: '#f5f5f5',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                    borderBottom: '2px solid #ddd',
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    borderRight: '1px solid #e0e0e0',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                                '& .MuiDataGrid-row': {
                                                    '&:hover': {
                                                        backgroundColor: '#f9f9f9',
                                                    },
                                                },
                                                '& .MuiDataGrid-virtualScroller': {
                                                    overflowX: 'auto',
                                                },
                                                '& .MuiDataGrid-row.Mui-disabled': {
                                                    opacity: 0.6,
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                            paginationModel={{
                                                pageSize: pageSize,
                                                page: page,
                                            }}
                                            rowCount={totalElements}
                                            rowSelectionModel={editIds}
                                            selectionsChanged={(ids) => {
                                                const selectedIDs = new Set(ids);
                                                setEditIds(Array.from(selectedIDs.values()) as number[]);
                                            }}
                                            columns={columns}
                                            onPaginationModelChange={(model) => {
                                                setPage(model.page);
                                                setPageSize(model.pageSize);
                                            }}
                                            paginationMode={'server'}
                                            sortingMode={'server'}
                                            onSortModelChange={setSortModel}
                                            rows={list === null ? [] : list}
                                            slots={{
                                                toolbar: GridToolbar,
                                                footer: () => (
                                                    <ShellCampaignsGridFooter
                                                        totalElements={totalElements}
                                                        numberOfElements={numberOfElements}
                                                    />
                                                ),
                                            }}
                                        />
                                    </Column>
                                )}
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={TABS.fees.slug}>
                        <CampaignFeesComponent
                            agencyView={true}
                            editMode={true}
                            allowEditToggle={false}
                            campaignFee={agency.campaignFee ? agency.campaignFee : new CampaignFee()}
                            onChange={(campaignFee: CampaignFee) => {
                                setAgency((prev) => {
                                    return { ...prev, campaignFee: campaignFee };
                                });
                            }}
                        />
                    </TabPanel>

                    <TabPanel value={selectedTab} index={TABS['dsp-integration-map'].slug}>
                        <AgencyDspIntegrationMap
                            dspEntities={amazonDspEntities}
                            dv360Entities={dv360Entities}
                            dspEntityIds={amazonDspEntityIds}
                            setDspEntityIds={setAmazonDspEntityIds}
                            dv360EntityIds={dv360EntityIds}
                            setDv360EntityIds={setDv360EntityIds}
                            sasAccountIds={sasAccountIds}
                            setSasAccountIds={setSasAccountIds}
                            sasAccounts={sasAccounts}
                        />
                    </TabPanel>

                    <TabPanel value={selectedTab} index={TABS['campaign-parameters'].slug}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <TextField
                                    name="cpTemplate"
                                    label="Campaign Parameter URL"
                                    value={agency.cpTemplate}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    helperText="Only required for Cognition Digital Agency"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>

                            <Grid size={4}>
                                <TextField
                                    name="cpSource"
                                    label="Source"
                                    value={agency.cpSource}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    name="cpMedium"
                                    label="Medium"
                                    value={agency.cpMedium}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    name="cpCampaign"
                                    label="Campaign"
                                    value={agency.cpCampaign}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    name="cpTerm"
                                    label="Term"
                                    value={agency.cpTerm}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    name="cpContent"
                                    label="Content"
                                    value={agency.cpContent}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={4} />
                        </Grid>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={TABS['first-party-data'].slug}>
                        <FirstPartyDataIntegration
                            agency={agency}
                            onChange={(value) => setAgency({ ...agency, ...value })}
                        />
                    </TabPanel>

                    <TabPanel value={selectedTab} index={TABS['reporting-exports'].slug}>
                        <AccessGuard
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                        >
                            <Grid size={7}>
                                <Grid>
                                    <Accordion
                                        sx={{
                                            mb: 2,
                                            '&:before': {
                                                display: 'none',
                                            },
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    mr: 2,
                                                }}
                                            >
                                                <Typography variant="h6" fontWeight={500}>
                                                    Scheduled S3 Exports
                                                </Typography>
                                                <ActiveStatus activeStatus={selectedReports.size > 0} />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                backgroundColor: '#fafafa',
                                                borderTop: '1px solid #e0e0e0',
                                                p: 3,
                                            }}
                                        >
                                            <Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        Platforms
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => setSelectScheduledPlatformsDialogOpen(true)}
                                                    >
                                                        Add Platforms
                                                    </Button>
                                                </Box>
                                                {selectedScheduledPlatforms.size > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        {PLATFORMS.filter((p) =>
                                                            selectedScheduledPlatforms.has(p.key)
                                                        ).map((platform) => (
                                                            <Chip
                                                                key={platform.key}
                                                                label={platform.name}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onDelete={() => {
                                                                    const next = new Set(selectedScheduledPlatforms);
                                                                    next.delete(platform.key);
                                                                    setSelectedScheduledPlatforms(next);
                                                                    if (agency.id) {
                                                                        ApiService.createExportSettings(
                                                                            agency.id,
                                                                            undefined,
                                                                            undefined,
                                                                            Array.from(next).join(',')
                                                                        ).then(() =>
                                                                            showInfoMessage(
                                                                                'success',
                                                                                'Platforms saved!'
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                <Divider sx={{ mb: 2 }} />

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        Reports
                                                    </Typography>
                                                    <Tooltip title={!agency.id ? 'Agency must first be created.' : ''}>
                                                        <span>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={!agency.id}
                                                                onClick={() => setSelectReportsDialogOpen(true)}
                                                            >
                                                                Add Reports
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                                {selectedReports.size > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        {REPORTS.filter((r) => selectedReports.has(r.key)).map(
                                                            (report) => (
                                                                <Chip
                                                                    key={report.key}
                                                                    label={report.name}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onDelete={() => {
                                                                        const next = new Set(selectedReports);
                                                                        next.delete(report.key);
                                                                        setSelectedReports(next);
                                                                        const sources = Array.from(next).join(',');
                                                                        if (agency.id) {
                                                                            ApiService.createExportSettings(
                                                                                agency.id,
                                                                                sources
                                                                            ).then(() => {
                                                                                showInfoMessage(
                                                                                    'success',
                                                                                    'Reporting Export settings saved!'
                                                                                );
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                            )
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            <SelectPlatformsDialog
                                                open={selectScheduledPlatformsDialogOpen}
                                                onClose={() => setSelectScheduledPlatformsDialogOpen(false)}
                                                hasAmazonDspEntities={amazonDspEntityIds.length > 0}
                                                hasDv360Entities={dv360EntityIds.length > 0}
                                                selectedPlatforms={selectedScheduledPlatforms}
                                                onConfirm={(platforms) => {
                                                    setSelectedScheduledPlatforms(platforms);
                                                    if (agency.id) {
                                                        ApiService.createExportSettings(
                                                            agency.id,
                                                            undefined,
                                                            undefined,
                                                            Array.from(platforms).join(',')
                                                        ).then(() => showInfoMessage('success', 'Platforms saved!'));
                                                    }
                                                }}
                                            />
                                            <SelectReportsDialog
                                                open={selectReportsDialogOpen}
                                                onClose={() => setSelectReportsDialogOpen(false)}
                                                agencyId={agency.id!}
                                                selectedReports={selectedReports}
                                                setSelectedReports={setSelectedReports}
                                                onSave={() => {
                                                    showInfoMessage('success', 'Reporting Export settings saved!');
                                                }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion
                                        sx={{
                                            mb: 2,
                                            '&:before': {
                                                display: 'none',
                                            },
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={500}>
                                                On Demand S3 Exports
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                backgroundColor: '#fafafa',
                                                borderTop: '1px solid #e0e0e0',
                                                p: 3,
                                            }}
                                        >
                                            <Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        Platforms
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => setSelectOnDemandPlatformsDialogOpen(true)}
                                                    >
                                                        Add Platforms
                                                    </Button>
                                                </Box>
                                                {selectedOnDemandPlatforms.size > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        {PLATFORMS.filter((p) =>
                                                            selectedOnDemandPlatforms.has(p.key)
                                                        ).map((platform) => (
                                                            <Chip
                                                                key={platform.key}
                                                                label={platform.name}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onDelete={() => {
                                                                    const next = new Set(selectedOnDemandPlatforms);
                                                                    next.delete(platform.key);
                                                                    setSelectedOnDemandPlatforms(next);
                                                                    if (agency.id) {
                                                                        ApiService.createExportSettings(
                                                                            agency.id,
                                                                            undefined,
                                                                            undefined,
                                                                            undefined,
                                                                            Array.from(next).join(',')
                                                                        ).then(() =>
                                                                            showInfoMessage(
                                                                                'success',
                                                                                'Platforms saved!'
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                <Divider sx={{ mb: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        Reports
                                                    </Typography>
                                                    <Tooltip title={!agency.id ? 'Agency must first be created.' : ''}>
                                                        <span>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={!agency.id}
                                                                onClick={() => setSelectOnDemandReportsDialogOpen(true)}
                                                            >
                                                                Add Reports
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                                {selectedOnDemandReports.size > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                        {REPORTS.filter((r) => selectedOnDemandReports.has(r.key)).map(
                                                            (report) => (
                                                                <Chip
                                                                    key={report.key}
                                                                    label={report.name}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onDelete={() => {
                                                                        const next = new Set(selectedOnDemandReports);
                                                                        next.delete(report.key);
                                                                        setSelectedOnDemandReports(next);
                                                                        const sourcesOndemand =
                                                                            Array.from(next).join(',');
                                                                        if (agency.id) {
                                                                            ApiService.createExportSettings(
                                                                                agency.id,
                                                                                undefined,
                                                                                sourcesOndemand
                                                                            ).then(() => {
                                                                                showInfoMessage(
                                                                                    'success',
                                                                                    'Reporting Export settings saved!'
                                                                                );
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                            )
                                                        )}
                                                    </Box>
                                                )}
                                                <Divider sx={{ mb: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        Month
                                                    </Typography>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        size="small"
                                                        label="Month Selector"
                                                        value={selectedMonth}
                                                        onChange={(event) => {
                                                            if (event.target.value) {
                                                                setSelectedMonth(Number(event.target.value));
                                                            }
                                                        }}
                                                    >
                                                        {renderExportOptions()}
                                                    </Select>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        gap: 2,
                                                        mt: 2,
                                                    }}
                                                >
                                                    <Button
                                                        color="primary"
                                                        type="submit"
                                                        disableFocusRipple
                                                        variant="contained"
                                                        startIcon={<UploadFile />}
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            if (agency.id && selectedOnDemandReports.size > 0) {
                                                                ApiService.createAdhocExport({
                                                                    id: 1,
                                                                    agencyId: agency.id,
                                                                    sources:
                                                                        Array.from(selectedOnDemandReports).join(','),
                                                                    platforms:
                                                                        selectedOnDemandPlatforms.size > 0
                                                                            ? Array.from(
                                                                                  selectedOnDemandPlatforms
                                                                              ).join(',')
                                                                            : undefined,
                                                                    range: selectedMonth.toString(),
                                                                }).then((response) => {
                                                                    showInfoMessage(
                                                                        'success',
                                                                        'Ad hoc reporting export has been submitted and is in progress… please allow up to 15 minutes for the data to be available on S3!'
                                                                    );
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        Export
                                                    </Button>
                                                </Box>
                                            </Box>
                                            <SelectReportsDialog
                                                open={selectOnDemandReportsDialogOpen}
                                                onClose={() => setSelectOnDemandReportsDialogOpen(false)}
                                                agencyId={agency.id!}
                                                selectedReports={selectedOnDemandReports}
                                                setSelectedReports={setSelectedOnDemandReports}
                                                saveField="sourcesOndemand"
                                                onSave={() => {
                                                    showInfoMessage('success', 'Reporting Export settings saved!');
                                                }}
                                            />
                                            <SelectPlatformsDialog
                                                open={selectOnDemandPlatformsDialogOpen}
                                                onClose={() => setSelectOnDemandPlatformsDialogOpen(false)}
                                                hasAmazonDspEntities={amazonDspEntityIds.length > 0}
                                                hasDv360Entities={dv360EntityIds.length > 0}
                                                selectedPlatforms={selectedOnDemandPlatforms}
                                                onConfirm={(platforms) => {
                                                    setSelectedOnDemandPlatforms(platforms);
                                                    if (agency.id) {
                                                        ApiService.createExportSettings(
                                                            agency.id,
                                                            undefined,
                                                            undefined,
                                                            undefined,
                                                            Array.from(platforms).join(',')
                                                        ).then(() => showInfoMessage('success', 'Platforms saved!'));
                                                    }
                                                }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion
                                        sx={{
                                            mb: 2,
                                            '&:before': {
                                                display: 'none',
                                            },
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    mr: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography variant="h6" fontWeight={500}>
                                                        Analytics API Access
                                                    </Typography>
                                                    <a
                                                        href="https://documenter.getpostman.com/view/32580146/2sA3JQ5Kgb"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1976d2' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Analytics API Documentation
                                                    </a>
                                                </Box>
                                                <ActiveStatus
                                                    activeStatus={!!agency.apiKey}
                                                    activeText="Configured"
                                                    inactiveText="Not Configured"
                                                />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                backgroundColor: '#fafafa',
                                                borderTop: '1px solid #e0e0e0',
                                                p: 3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle1" fontWeight={500}>
                                                    Platforms
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => setSelectScheduledPlatformsDialogOpen(true)}
                                                >
                                                    Add Platforms
                                                </Button>
                                            </Box>
                                            {selectedScheduledPlatforms.size > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                    {PLATFORMS.filter((p) => selectedScheduledPlatforms.has(p.key)).map(
                                                        (platform) => (
                                                            <Chip
                                                                key={platform.key}
                                                                label={platform.name}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onDelete={() => {
                                                                    const next = new Set(selectedScheduledPlatforms);
                                                                    next.delete(platform.key);
                                                                    setSelectedScheduledPlatforms(next);
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                            <Divider sx={{ mb: 2 }} />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle1" fontWeight={500}>
                                                    Enable API Docs in Client Account
                                                </Typography>
                                                <Tooltip
                                                    title={!agency.apiKey ? 'Generate an API Key first' : 'API Docs'}
                                                >
                                                    <span>
                                                        <Switch
                                                            checked={agency.activeApiDocs}
                                                            onChange={handleChange}
                                                            name="activeApiDocs"
                                                            disabled={!agency.apiKey}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                            <Divider sx={{ mb: 2 }} />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle1" fontWeight={500}>
                                                    Include Cost Metrics
                                                </Typography>
                                                {/* Cost Metrics Toggle */}
                                                <Tooltip
                                                    title={!agency.apiKey ? 'Generate an API Key first' : 'Cost Metric'}
                                                >
                                                    <span>
                                                        <Switch
                                                            checked={agency.apiIncludeCostMetrics}
                                                            name="apiIncludeCostMetrics"
                                                            onChange={handleChange}
                                                            disabled={!agency.apiKey}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                            <Divider sx={{ mb: 2 }} />
                                            <Box sx={{ mb: 1 }}>
                                                {/* API Key */}
                                                <TextField
                                                    name="apiKey"
                                                    fullWidth
                                                    size="small"
                                                    label="API Key"
                                                    variant="outlined"
                                                    value={agency.apiKey || ''}
                                                    slotProps={{
                                                        input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {action}
                                                                </InputAdornment>
                                                            ),
                                                            endAdornment: agency.apiKey ? (
                                                                <InputAdornment position="end">
                                                                    <Tooltip title="Copy API Key">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(
                                                                                    agency.apiKey || ''
                                                                                );
                                                                                showInfoMessage(
                                                                                    'success',
                                                                                    'API Key copied to clipboard.'
                                                                                );
                                                                            }}
                                                                        >
                                                                            <ContentCopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </InputAdornment>
                                                            ) : undefined,
                                                        },
                                                        inputLabel: { shrink: true },
                                                    }}
                                                />
                                            </Box>
                                            <Divider sx={{ mb: 2 }} />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    gap: 2,
                                                    mt: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="text"
                                                    onClick={() => {
                                                        setAgency({
                                                            ...agency,
                                                            activeApiDocs: savedApiAccessState.activeApiDocs,
                                                            apiIncludeCostMetrics:
                                                                savedApiAccessState.apiIncludeCostMetrics,
                                                            apiKey: savedApiAccessState.apiKey,
                                                        });
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        ApiService.updateAgency(agency)
                                                            .then((response) => {
                                                                setAgency(response.data);
                                                                setSavedApiAccessState({
                                                                    activeApiDocs: response.data.activeApiDocs ?? false,
                                                                    apiIncludeCostMetrics:
                                                                        response.data.apiIncludeCostMetrics ?? false,
                                                                    apiKey: response.data.apiKey ?? null,
                                                                });
                                                                showInfoMessage(
                                                                    'success',
                                                                    'API Access settings saved!'
                                                                );
                                                            })
                                                            .catch(() => {});
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            </Grid>
                        </AccessGuard>
                    </TabPanel>

                    <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                        <TabPanel index={TABS['access-permissions'].slug} value={selectedTab}>
                            <AccessPermissions agency={agency} setAgency={setAgency} onChange={handleChange} />
                        </TabPanel>
                    </AccessGuard>

                    {selectedTab !== TABS['reporting-exports'].slug && (
                        <Grid container spacing={2}>
                            <Grid
                                style={{ marginTop: '30px' }}
                                size={{
                                    md: 12,
                                }}
                            >
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disableFocusRipple
                                        variant="contained"
                                        startIcon={<SaveOutlined />}
                                    >
                                        Save
                                    </Button>
                                    <Link to="/agencies">
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            disableFocusRipple
                                            startIcon={<CloseOutlined />}
                                        >
                                            Cancel
                                        </Button>
                                    </Link>
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                </form>
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={editModalStyles}>
                        <Box sx={{ width: '100%' }}>
                            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                Bulk Edit
                            </Typography>
                            <form onSubmit={bulkChangeSubmit}>
                                <Typography id="modal-modal-description">
                                    <Box sx={{}}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography>DSP Advertiser ID</Typography>
                                            <TextField
                                                id="outlined-basic"
                                                label="DSP Advertiser ID"
                                                variant="outlined"
                                                value={dspAdvertiserID}
                                                size="small"
                                                onChange={(e) => setDspAdvertiserID(e.target.value)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography>Platform</Typography>
                                            <TextField
                                                id="outlined-basic"
                                                label="Platform"
                                                variant="outlined"
                                                value={platform}
                                                size="small"
                                                onChange={(e) => setPlatform(e.target.value)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography>Oem Program</Typography>
                                            <TextField
                                                id="outlined-basic"
                                                label="Oem Program"
                                                variant="outlined"
                                                value={oemProgram}
                                                size="small"
                                                onChange={(e) => setOemProgram(e.target.value)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography>PO Number</Typography>
                                            <TextField
                                                id="outlined-basic"
                                                label="PO Number"
                                                variant="outlined"
                                                value={poNumber}
                                                size="small"
                                                onChange={(e) => setPoNumber(e.target.value)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography>DSP Campaign ID</Typography>
                                            <TextField
                                                id="outlined-basic"
                                                label="DSP Campaign ID"
                                                variant="outlined"
                                                value={dspCampaignID}
                                                size="small"
                                                onChange={(e) => setDspCampaignID(e.target.value)}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-evenly',
                                            marginTop: '10%',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            sx={{ width: '120px' }}
                                            startIcon={<SaveIcon />}
                                            type="submit"
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            sx={{ width: '120px' }}
                                            onClick={handleClose}
                                            startIcon={<CloseIcon />}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Typography>
                            </form>
                        </Box>
                    </Box>
                </Modal>
            </Block>
        </>
    );
}
